import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Input,
  Button,
  List,
  message,
  Popconfirm,
  Space,
  Tag,
  Spin,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../config/api.js";

export interface PosServer {
  id: number;
  name: string;
  phone: string | null;
  is_active: boolean;
}

interface ServerManagerModalProps {
  open: boolean;
  onClose: () => void;
  companyId: number | null;
  warehouseId: number | null;
  /** Appele apres toute creation / modification / desactivation pour rafraichir la liste du POS */
  onChanged?: () => void;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const ServerManagerModal: React.FC<ServerManagerModalProps> = ({
  open,
  onClose,
  companyId,
  warehouseId,
  onChanged,
}) => {
  const [servers, setServers] = useState<PosServer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  // Creation
  const [newName, setNewName] = useState<string>("");
  const [newPhone, setNewPhone] = useState<string>("");

  // Edition inline
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editPhone, setEditPhone] = useState<string>("");

  const fetchServers = useCallback(async () => {
    if (!companyId) {
      setServers([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(API_CONFIG.apiUrl("/pos/servers"), {
        params: {
          company_id: companyId,
          warehouse_id: warehouseId || undefined,
          include_inactive: true,
        },
        headers: getAuthHeaders(),
      });
      setServers(
        Array.isArray(response.data?.servers) ? response.data.servers : []
      );
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Impossible de charger la liste des serveurs."
      );
      setServers([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, warehouseId]);

  useEffect(() => {
    if (open) {
      void fetchServers();
      setNewName("");
      setNewPhone("");
      setEditingId(null);
    }
  }, [open, fetchServers]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      message.warning("Le nom du serveur est requis.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        API_CONFIG.apiUrl("/pos/servers"),
        {
          company_id: companyId,
          warehouse_id: warehouseId || null,
          name,
          phone: newPhone.trim() || null,
        },
        { headers: getAuthHeaders() }
      );
      message.success("Serveur ajoute.");
      setNewName("");
      setNewPhone("");
      await fetchServers();
      onChanged?.();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Impossible d'ajouter le serveur."
      );
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (server: PosServer) => {
    setEditingId(server.id);
    setEditName(server.name);
    setEditPhone(server.phone || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditPhone("");
  };

  const handleUpdate = async (serverId: number) => {
    const name = editName.trim();
    if (!name) {
      message.warning("Le nom du serveur ne peut pas etre vide.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        API_CONFIG.apiUrl(`/pos/servers/${serverId}`),
        { company_id: companyId, name, phone: editPhone.trim() || null },
        { headers: getAuthHeaders() }
      );
      message.success("Serveur mis a jour.");
      cancelEdit();
      await fetchServers();
      onChanged?.();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Impossible de modifier le serveur."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (server: PosServer, isActive: boolean) => {
    setSaving(true);
    try {
      if (isActive) {
        await axios.put(
          API_CONFIG.apiUrl(`/pos/servers/${server.id}`),
          { company_id: companyId, is_active: true },
          { headers: getAuthHeaders() }
        );
      } else {
        await axios.delete(API_CONFIG.apiUrl(`/pos/servers/${server.id}`), {
          params: { company_id: companyId },
          headers: getAuthHeaders(),
        });
      }
      message.success(isActive ? "Serveur reactive." : "Serveur desactive.");
      await fetchServers();
      onChanged?.();
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Impossible de changer l'etat du serveur."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Gestion des serveurs"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
      ]}
      destroyOnClose
    >
      {/* Ajout d'un serveur */}
      <Space.Compact style={{ width: "100%", marginBottom: 8 }}>
        <Input
          placeholder="Nom du serveur"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleCreate}
          maxLength={120}
        />
        <Input
          placeholder="Telephone (optionnel)"
          value={newPhone}
          onChange={(e) => setNewPhone(e.target.value)}
          onPressEnter={handleCreate}
          maxLength={40}
          style={{ maxWidth: 170 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          loading={saving}
        >
          Ajouter
        </Button>
      </Space.Compact>

      <Spin spinning={loading}>
        {servers.length === 0 ? (
          <Empty description="Aucun serveur" />
        ) : (
          <List
            size="small"
            dataSource={servers}
            renderItem={(server) => {
              const isEditing = editingId === server.id;
              return (
                <List.Item
                  actions={
                    isEditing
                      ? [
                          <Button
                            key="save"
                            type="link"
                            icon={<SaveOutlined />}
                            onClick={() => handleUpdate(server.id)}
                            loading={saving}
                          >
                            Enregistrer
                          </Button>,
                          <Button
                            key="cancel"
                            type="link"
                            icon={<CloseOutlined />}
                            onClick={cancelEdit}
                          >
                            Annuler
                          </Button>,
                        ]
                      : [
                          <Button
                            key="edit"
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => startEdit(server)}
                          >
                            Modifier
                          </Button>,
                          server.is_active ? (
                            <Popconfirm
                              key="deactivate"
                              title="Desactiver ce serveur ?"
                              okText="Desactiver"
                              cancelText="Annuler"
                              onConfirm={() => handleSetActive(server, false)}
                            >
                              <Button type="link" danger icon={<CloseOutlined />}>
                                Desactiver
                              </Button>
                            </Popconfirm>
                          ) : (
                            <Button
                              key="reactivate"
                              type="link"
                              icon={<UndoOutlined />}
                              onClick={() => handleSetActive(server, true)}
                            >
                              Reactiver
                            </Button>
                          ),
                        ]
                  }
                >
                  {isEditing ? (
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={120}
                      />
                      <Input
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        placeholder="Telephone"
                        maxLength={40}
                        style={{ maxWidth: 170 }}
                      />
                    </Space.Compact>
                  ) : (
                    <Space>
                      <span style={{ fontWeight: 500 }}>{server.name}</span>
                      {server.phone ? (
                        <Tag color="default">{server.phone}</Tag>
                      ) : null}
                      {!server.is_active ? (
                        <Tag color="red">Inactif</Tag>
                      ) : null}
                    </Space>
                  )}
                </List.Item>
              );
            }}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default ServerManagerModal;
